import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Menu, Modal } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  DownOutlined,
  RetweetOutlined,
  UserDeleteOutlined
} from "@ant-design/icons";

import { setSuccessMessage } from "app/global/slice";
import {
  assignDriver,
  reassignDriver,
  unassignDriver
} from "modules/admin/orders/store/RouteThunks";
import DriverSearchSelect from "./DriverSearchSelect";
import useAssignableDrivers from "./useAssignableDrivers";

const ACTIONS = { ASSIGN: "assign", REASSIGN: "reassign", UNASSIGN: "unassign" };

// Same status rules as the Active Routes table actions.
const getRouteDriverId = (route) => route?.driverId || route?.driver?.id;

export const getDriverAssignActions = (route) => {
  const hasDriver = !!getRouteDriverId(route);
  const actions = [];
  if (!hasDriver && route?.status === "pending") actions.push(ACTIONS.ASSIGN);
  if (hasDriver && ["accepted", "in-progress"].includes(route?.status)) {
    actions.push(ACTIONS.REASSIGN);
  }
  // Unassign resets every stop to "unassigned", so only allow it before the route starts.
  if (hasDriver && route?.status === "accepted") actions.push(ACTIONS.UNASSIGN);
  return actions;
};

const RouteDriverAssign = ({ route, isAdmin, onChanged }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.global.isLoading);
  const [mode, setMode] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState();
  const currentDriverId = getRouteDriverId(route);
  const actions = getDriverAssignActions(route);
  const { drivers, loading } = useAssignableDrivers(
    isAdmin,
    actions.length > 0,
    route?.clientId
  );

  if (!actions.length) return null;

  const openModal = (nextMode) => {
    setSelectedDriverId(nextMode === ACTIONS.REASSIGN ? currentDriverId : undefined);
    setMode(nextMode);
  };

  const handleSubmit = async () => {
    if (!selectedDriverId) return;
    const isReassign = mode === ACTIONS.REASSIGN;
    const { error } = await dispatch(
      (isReassign ? reassignDriver : assignDriver)({
        routeId: route?.id,
        driverId: selectedDriverId
      })
    );
    if (!error) {
      dispatch(
        setSuccessMessage(`${isReassign ? "Reassigned" : "Assigned"} successfully!`)
      );
      setMode(null);
      onChanged && onChanged();
    }
  };

  const handleUnassign = () => {
    Modal.confirm({
      title: "Are you sure to unassign this driver?",
      content: "The route will go back to pending and all stops will be unassigned.",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        const { error } = await dispatch(unassignDriver(route?.id));
        if (!error) {
          dispatch(setSuccessMessage("Driver removed successfully!"));
          onChanged && onChanged();
        }
      }
    });
  };

  const handleMenuClick = ({ key }) => {
    if (key === ACTIONS.UNASSIGN) handleUnassign();
    else openModal(key);
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      {actions.includes(ACTIONS.ASSIGN) && (
        <Menu.Item key={ACTIONS.ASSIGN} icon={<ArrowRightOutlined />}>
          Assign
        </Menu.Item>
      )}
      {actions.includes(ACTIONS.REASSIGN) && (
        <Menu.Item key={ACTIONS.REASSIGN} icon={<RetweetOutlined />}>
          Reassign
        </Menu.Item>
      )}
      {actions.includes(ACTIONS.UNASSIGN) && (
        <Menu.Item key={ACTIONS.UNASSIGN} icon={<UserDeleteOutlined />} danger>
          Unassign
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      <Dropdown overlay={menu} trigger={["click"]}>
        <Button className="mt-3" block>
          Assign <DownOutlined />
        </Button>
      </Dropdown>
      {mode && (
        <Modal
          closable={false}
          visible={true}
          title={`${mode === ACTIONS.REASSIGN ? "Reassign" : "Assign"} Driver`}
          width={600}
          footer={
            <div className="buttons-modal">
              <Button size="large" onClick={() => setMode(null)}>
                Cancel
              </Button>
              <Button
                disabled={!selectedDriverId || selectedDriverId === currentDriverId}
                loading={isLoading}
                size="large"
                type="primary"
                onClick={handleSubmit}
              >
                Assign
                <CheckCircleOutlined />
              </Button>
            </div>
          }
        >
          <DriverSearchSelect
            size="large"
            placeholder="Search driver by name or email"
            drivers={drivers}
            loading={loading}
            value={selectedDriverId}
            onChange={setSelectedDriverId}
            showInternalTag={isAdmin}
          />
        </Modal>
      )}
    </>
  );
};

export default RouteDriverAssign;
